// Token model and lifecycle helpers: trading through curve/AMM, holder ledger,
// candles, graduation. The holder ledger is the single source of truth for balances.

import type { SwapResult } from "./bondingCurve";
import { TUNABLES } from "@degen/shared/config/tunables";
import { createCurve, curveBuy, curvePrice, curveQuoteBuy, curveQuoteSell, curveSell, type Curve } from "./bondingCurve";
import { ammBuy, ammQuoteBuy, ammQuoteSell, ammSell, poolPrice, type Pool } from "./amm";

export type Fate = "normal" | "runner" | "rug" | "slow_bleed";
export type TokenPhase = "curve" | "amm" | "dead";

export interface Candle {
  t: number; // tick
  o: number;
  h: number;
  l: number;
  c: number;
  v: number; // USD volume
}

export interface Trade {
  t: number;
  who: string;
  side: "buy" | "sell";
  usd: number;
  tokens: number;
}

export interface Token {
  id: string;
  name: string;
  ticker: string;
  creator: "player" | string; // "player" or an NPC dev id
  description: string;
  iconSeed: number;
  phase: TokenPhase;
  curve?: Curve;
  pool?: Pool;
  totalSupply: number;
  devBagPct: number; // dev's share of supply at launch (shown to players)
  hype: number; // 0-100
  candles: Candle[];
  // SPEC-NOTE: spec uses Map<ownerId, number>; a plain record keeps saves JSON-native.
  holders: Record<string, number>;
  /** Shared worlds: the client only mirrors its own balance; the server sends the holder count. */
  holderCount?: number;
  bornTick: number;
  launchPrice: number;
  fate: Fate; // hidden for NPC tokens; "normal" for player tokens
  // --- sim internals ---
  devId: string;
  rugTick?: number;
  rugChunksLeft?: number;
  rugged?: boolean;
  /** Runners that go parabolic (hidden, like fate). */
  moonshot?: boolean;
  moonshotTarget?: number; // peak multiple outside money chases it to
  peakMcap: number;
  lowSinceTick: number | null;
  graduatedTick?: number;
  diedTick?: number;
  volumeUsd: number;
  trades: Trade[];
}

export interface TradeResult {
  ok: boolean;
  usd: number; // buy: total USD spent incl. fee; sell: USD received after fee
  tokens: number; // buy: tokens received; sell: tokens sold
  fee: number;
  graduated?: boolean;
}

const FAIL: TradeResult = { ok: false, usd: 0, tokens: 0, fee: 0 };

export function createToken(args: {
  id: string;
  name: string;
  ticker: string;
  creator: string;
  description: string;
  iconSeed: number;
  fate: Fate;
  hype: number;
  bornTick: number;
}): Token {
  const curve = createCurve();
  const price = curvePrice(curve);
  return {
    ...args,
    phase: "curve",
    curve,
    launchPrice: price,
    totalSupply: TUNABLES.market.totalSupply,
    devBagPct: 0,
    candles: [{ t: args.bornTick, o: price, h: price, l: price, c: price, v: 0 }],
    holders: {},
    devId: `dev:${args.id}`,
    peakMcap: price * TUNABLES.market.totalSupply,
    lowSinceTick: null,
    volumeUsd: 0,
    trades: [],
  };
}

export function priceOf(t: Token): number {
  if (t.phase === "dead") return 0; // SPEC-NOTE: dead bags are worthless and untradeable
  if (t.pool) return poolPrice(t.pool);
  if (t.curve) return curvePrice(t.curve);
  return 0;
}

export function mcapOf(t: Token): number {
  return priceOf(t) * t.totalSupply;
}

/** Last traded price even for dead tokens (for charts). */
export function lastPrice(t: Token): number {
  return t.candles.length ? t.candles[t.candles.length - 1].c : 0;
}

export function balanceOf(t: Token, who: string): number {
  return t.holders[who] ?? 0;
}

export function holderCount(t: Token): number {
  if (t.holderCount !== undefined) return t.holderCount; // shared-world mirror (server count)
  let n = 0;
  for (const k in t.holders) if (t.holders[k] > 0) n++;
  return n;
}

/** Curve progress toward graduation, 0-1. */
export function curveProgress(t: Token): number {
  if (t.phase !== "curve") return 1;
  return Math.min(1, mcapOf(t) / TUNABLES.market.graduationMcap);
}

export interface Quote {
  tokens: number;
  usd: number;
  fee: number;
  priceImpact: number; // fractional move in spot price caused by the trade
}

export function quoteBuy(t: Token, usd: number): Quote {
  const empty = { tokens: 0, usd: 0, fee: 0, priceImpact: 0 };
  if (t.phase === "dead" || !(usd > 0)) return empty;
  const fee = usd * TUNABLES.market.tradeFee;
  const net = usd - fee;
  const p0 = priceOf(t);
  if (t.phase === "curve" && t.curve) {
    const r = curveQuoteBuy(t.curve, net);
    if (r.out <= 0) return empty;
    const p1 = (t.curve.vUsd + r.in) / (t.curve.vTokens - r.out);
    return { tokens: r.out, usd: r.in + fee, fee, priceImpact: p1 / p0 - 1 };
  }
  if (t.pool) {
    const r = ammQuoteBuy(t.pool, net);
    if (r.out <= 0) return empty;
    const p1 = (t.pool.usd + r.in) / (t.pool.tokens - r.out);
    return { tokens: r.out, usd, fee, priceImpact: p1 / p0 - 1 };
  }
  return empty;
}

export function quoteSell(t: Token, tokens: number): Quote {
  const empty = { tokens: 0, usd: 0, fee: 0, priceImpact: 0 };
  if (t.phase === "dead" || !(tokens > 0)) return empty;
  const p0 = priceOf(t);
  let r: SwapResult;
  let p1: number;
  if (t.phase === "curve" && t.curve) {
    r = curveQuoteSell(t.curve, tokens);
    p1 = (t.curve.vUsd - r.out) / (t.curve.vTokens + r.in);
  } else if (t.pool) {
    r = ammQuoteSell(t.pool, tokens);
    p1 = (t.pool.usd - r.out) / (t.pool.tokens + r.in);
  } else return empty;
  if (r.out <= 0) return empty;
  const fee = r.out * TUNABLES.market.tradeFee;
  return { tokens: r.in, usd: r.out - fee, fee, priceImpact: p1 / p0 - 1 };
}

function recordTrade(t: Token, tick: number, who: string, side: "buy" | "sell", usd: number, tokens: number) {
  t.volumeUsd += usd;
  t.trades.push({ t: tick, who, side, usd, tokens });
  if (t.trades.length > TUNABLES.market.tradesCap) t.trades.shift();
  touchCandle(t, tick, usd);
  const mcap = mcapOf(t);
  if (mcap > t.peakMcap) t.peakMcap = mcap;
}

/** Ensure a candle exists for `tick` and fold the current price into it. */
export function touchCandle(t: Token, tick: number, volume = 0) {
  const price = priceOf(t);
  const last = t.candles[t.candles.length - 1];
  if (!last || last.t !== tick) {
    const o = last ? last.c : price;
    t.candles.push({ t: tick, o, h: Math.max(o, price), l: Math.min(o, price), c: price, v: volume });
    if (t.candles.length > TUNABLES.market.candleCap) t.candles.shift();
    return;
  }
  last.c = price;
  if (price > last.h) last.h = price;
  if (price < last.l) last.l = price;
  last.v += volume;
}

function credit(t: Token, who: string, amount: number) {
  t.holders[who] = (t.holders[who] ?? 0) + amount;
}

function debit(t: Token, who: string, amount: number) {
  const left = (t.holders[who] ?? 0) - amount;
  if (left < TUNABLES.market.dustTokens) delete t.holders[who];
  else t.holders[who] = left;
}

/** Spend `usd` (fee included) to buy tokens for `who`. Caller handles the buyer's cash. */
export function executeBuy(t: Token, who: string, usd: number, tick: number): TradeResult {
  if (t.phase === "dead" || !(usd > 0) || !Number.isFinite(usd)) return FAIL;
  const fee = usd * TUNABLES.market.tradeFee;
  const net = usd - fee;
  let got: SwapResult;
  if (t.phase === "curve" && t.curve) got = curveBuy(t.curve, net);
  else if (t.pool) got = ammBuy(t.pool, net);
  else return FAIL;
  if (got.out <= 0) return FAIL;
  // At the curve supply cap only part of the USD is used; the fee scales with it.
  const spent = got.in + got.in * (fee / net);
  credit(t, who, got.out);
  recordTrade(t, tick, who, "buy", spent, got.out);
  const graduated = maybeGraduate(t, tick);
  return { ok: true, usd: spent, tokens: got.out, fee: spent - got.in, graduated };
}

/** Sell up to `tokens` held by `who`. Caller credits the returned USD. */
export function executeSell(t: Token, who: string, tokens: number, tick: number): TradeResult {
  if (t.phase === "dead" || !(tokens > 0) || !Number.isFinite(tokens)) return FAIL;
  const amount = Math.min(tokens, balanceOf(t, who));
  if (amount <= 0) return FAIL;
  let got: SwapResult;
  if (t.phase === "curve" && t.curve) got = curveSell(t.curve, amount);
  else if (t.pool) got = ammSell(t.pool, amount);
  else return FAIL;
  if (got.out <= 0) return FAIL;
  const fee = got.out * TUNABLES.market.tradeFee;
  debit(t, who, got.in);
  recordTrade(t, tick, who, "sell", got.out, got.in);
  return { ok: true, usd: got.out - fee, tokens: got.in, fee };
}

/** Move real curve reserves into an AMM pool at the same spot price. */
export function maybeGraduate(t: Token, tick: number): boolean {
  if (t.phase !== "curve" || !t.curve) return false;
  if (mcapOf(t) < TUNABLES.market.graduationMcap) return false;
  const price = curvePrice(t.curve);
  // SPEC-NOTE: pool gets the real USD plus enough tokens to keep the price continuous;
  // unsold curve tokens beyond that are burned.
  t.pool = { usd: t.curve.realUsd, tokens: t.curve.realUsd / price };
  t.curve = undefined;
  t.phase = "amm";
  t.graduatedTick = tick;
  return true;
}

export function killToken(t: Token, tick: number) {
  t.phase = "dead";
  t.diedTick = tick;
  t.hype = 0;
}

/** Move tokens between holders (airdrops, TGE distribution). Returns the amount moved. */
export function transferTokens(t: Token, from: string, to: string, amount: number): number {
  const amt = Math.min(amount, balanceOf(t, from));
  if (!(amt > 0)) return 0;
  debit(t, from, amt);
  credit(t, to, amt);
  return amt;
}

/** Give tokens out of thin air (TGE allocations). */
export function mintTo(t: Token, to: string, amount: number) {
  if (amount > 0) credit(t, to, amount);
}

/** Turn a fresh token into one already trading on an AMM pool at `price` (used for TGEs). */
export function seedAmm(t: Token, poolUsd: number, price: number, tick: number) {
  t.curve = undefined;
  t.pool = { usd: poolUsd, tokens: poolUsd / price };
  t.phase = "amm";
  t.graduatedTick = tick;
  t.launchPrice = price;
  t.candles = [{ t: tick, o: price, h: price, l: price, c: price, v: 0 }];
  t.peakMcap = price * t.totalSupply;
}
